import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {
    DataGridColumnAlignments,
    DataGridColumnTypes,
    DataGridColumnWidthTypes,
    DataGridDensities,
    DataGridExoticColumnFields
} from "../../../../../../../packages/data-grid";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {DecreaseSerializedProductAvailabilityDataGridCellKeys} from "../../../../../../../core/constants/table/data-grid-column-keys";
import {formatMoney} from "../../../../../../../core/services/utils/utils";
import {Col} from "reactstrap";
import SearchInput from "../../../../../base/input/search_input";
import PartnerDataGrid, {useDataGridSavedState} from "../../../../../../containers/data-grid";
import ReasonNoteSection from "../../reason-note-section";
import {OutlinedInput} from "../../../../../base/input/outlined_input";
import {Paper} from "@mui/material";
import Api from "../../../../../../../core/services/api/api";

const _initialPaginationInfo = {
    pageSize: 5,
    currentPage: 1,
    length: 0,
    sizes: [5, 10, 15],
};


const dataGridSaveConfig = {
    name: 'DecreaseProductAvailabilityDialogSerializableSectionDataGrid',
    version: 3,
};

const DecreaseProductAvailabilityDialogSerializableSection = ({
                                                                  hideReasonNoteSection,
                                                                  partNo,
                                                                  currentAvailability,
                                                                  selectedSerialNos,
                                                                  setSelectedSerialNos,
                                                                  modifying,
                                                                  showLastSection,
                                                                  formId,
                                                                  onSubmitReasonNotesForm,
                                                                  keyInformation,
                                                              }) => {
    const [loading, setLoading] = useState(true);
    const [fetchingAll, setFetchingAll] = useState(false);
    const [entries, setEntries] = useState([]);
    const [keywords, setKeywords] = useState('');
    const isMounted = useIsMounted();
    /** @type {React.MutableRefObject<DataGridController>}*/
    const dataGridController = useRef();

    const columns = useMemo(() =>
        /**@type {DataGridColumn[]}*/
        [
            {
                name: DataGridExoticColumnFields.selection,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                resizable: false,
                visibilityToggleable: false,
                pinnedToggleable: false,
                reorderable: false,
                sortable: false,
                pinned: false,
            },
            {
                title: "Serials",
                name: DecreaseSerializedProductAvailabilityDataGridCellKeys.serial,
                type: DataGridColumnTypes.string,
                width: 150
            },
            {
                title: "Cost",
                name: DecreaseSerializedProductAvailabilityDataGridCellKeys.cost,
                type: DataGridColumnTypes.element,
                alignment: DataGridColumnAlignments.right,
                sortable: false,
                width: 70
            },
            {
                title: "Received Date",
                name: DecreaseSerializedProductAvailabilityDataGridCellKeys.receivedDate,
                type: DataGridColumnTypes.date,
                width: {
                    type: DataGridColumnWidthTypes.flex,
                    size: 1,
                },
            }
        ], [])

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
        sortByObjectMap: {
            property: 'field',
            descending: 'descending',
        },
    })

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ..._initialPaginationInfo,
        pageSize: dataGridPageSize
    } : _initialPaginationInfo, [dataGridPageSize]);

    const [paginationInfo, setPaginationInfo] = useState(initialPaginationInfo);
    const [orderBy, setOrderBy] = useState(dataGridSortBy);
    const paginationRef = useRef(paginationInfo);
    paginationRef.current = paginationInfo;

    /**
     * With each change in the [showLastSection] and [entries]:
     * Updates the selections of the data grid by toggling dataGridController rows selection
     */
    useEffect(() => {
        if (showLastSection || entries.length === 0 || loading)
            return;
        setTimeout(() => {
            if (!isMounted())
                return;
            if ((selectedSerialNos?.length ?? 0) === paginationRef.current.length) {
                dataGridController.current.toggleAllRowsSelection(true);
            } else {
                dataGridController.current.toggleAllRowsSelection(false);
                dataGridController.current.toggleSelectedRows(Object.fromEntries(selectedSerialNos.map(e => [e?.serialNo, true])))
            }
        }, 300)
    }, [showLastSection, entries, loading, isMounted])

    /**
     * Fetches the serial numbers of the current product from the server.
     * @type {(function(): Promise<void>)}
     */
    const getSerialNumbersOfProduct = useCallback(async () => {
        setLoading(true);
        const forApi = {
            partNumber: partNo,
            warehouseCode: keyInformation.warehouse,
            keyword: keywords,
            pagination: {
                currentPage: paginationInfo?.currentPage,
                pageSize: paginationInfo?.pageSize,
            },
            sorts: [
                ...(orderBy ? [{property: orderBy.field, isDesc: orderBy.descending}] : [])
            ]
        }
        const response = await Api.getInventoryAdjustmentProductSerialNumbers(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setEntries(response.data?.items ?? []);
            setPaginationInfo(prevState => ({...prevState, length: response.data.totalCount}))
        }
        setLoading(false);
    }, [isMounted, keyInformation.warehouse, keywords, orderBy, paginationInfo?.currentPage, paginationInfo?.pageSize, partNo])

    /**
     * With each change in the [partNo] or [warehouse] prop values:
     * - fetches the serial numbers of the product from the server.
     */
    useEffect(() => {
        if (!partNo)
            return;
        getSerialNumbersOfProduct().then();
    }, [getSerialNumbersOfProduct, partNo]);

    /**
     * Sorts the entries of the credits in place.
     * @param {DataGridSortBy} tableOrderBy
     */
    const onSort = useCallback((tableOrderBy) => {
        setOrderBy(tableOrderBy);
        setPaginationInfo(prevState => ({...prevState, currentPage: 1}));
    }, [])

    /**
     * Fetches all the entries of the serial numbers of the product from the server.
     * @type {(function(): Promise<[]|*>)}
     */
    const fetchAllEntries = useCallback(async () => {
        setFetchingAll(true);
        const forApi = {
            partNumber: partNo,
            warehouseCode: keyInformation.warehouse,
            keyword: keywords,
            pagination: {currentPage: 1, pageSize: paginationInfo.length},
        }
        const response = await Api.getInventoryAdjustmentProductSerialNumbers(forApi);
        if (!isMounted())
            return [];
        setFetchingAll(false);
        return response?.data?.items ?? [];
    }, [isMounted, keyInformation?.warehouse, keywords, paginationInfo?.length, partNo])

    /**
     * Sets the [selectedSerialNos]'s state value by each change in the selection state of the data-grid.
     * @type {(prev?: DataGridRowSelectionInfo, current?: DataGridRowSelectionInfo) => void}
     */
    const onRowSelectionInfoChanged = useCallback(async (prev, current) => {
        if (current.allRowsSelected) {
            const excludedSerialNos = current.excludedRows.map(e => e.data.serialNumber);
            const allEntries = await fetchAllEntries();
            setSelectedSerialNos(allEntries.filter(e => !excludedSerialNos.includes(e.serialNumber)).map(e => ({
                cost: e.unitCost,
                serialNo: e.serialNumber,
            })));
        } else {
            setSelectedSerialNos(current.selectedRows.map(e => ({
                serialNo: e.data.serialNumber,
                cost: e.data.unitCost,
            })));
        }
    }, [setSelectedSerialNos, fetchAllEntries]);

    /**
     * Changes the value of cost field in a serial number record:
     * if the input value is invalid, updates the serial number cost in entries list with empty string
     * otherwise, sets it with the parse value
     * @param serialNumber
     * @param value
     */
    const handleSerialCostChange = (serialNumber, value) => {
        const parsedValue = parseFloat(value);
        let parsedPrice;
        if (isNaN(parsedValue)) {
            setEntries(prevState => prevState.map(e => e.serialNumber === serialNumber ? {...e, unitCost: ''} : e))
            return;
        } else if (parsedValue < 0) {
            parsedPrice = -1 * parsedValue;
        } else {
            parsedPrice = parsedValue;
        }
        setEntries(prevState => prevState.map(e => e.serialNumber === serialNumber ? {...e, unitCost: parsedPrice} : e))
    }

    /**
     * Sets the serial number cost in the list of entries with its previous value if data was invalid when text field was blurred
     * Otherwise, updates the cost of the selected serial number in the selectedSerialNos list
     * @param e
     * @param serialNumber
     */
    const handleSerialCostBlurred = useCallback((e, serialNumber) => {
        const parsedValue = parseFloat(e.target.value);
        if (isNaN(parsedValue)) {
            setEntries(prevState => prevState.map(e => e.serialNumber === serialNumber
                ? {...e, unitCost: selectedSerialNos.find(t => t.serialNo === serialNumber).cost}
                : e
            ))
            return;
        }
        setSelectedSerialNos(prevState =>
            prevState.map(e => e.serialNo === serialNumber ? {
                ...e,
                serialNo: e.serialNumber ?? e.serialNo,
                cost: parsedValue,
            } : e));
    }, [selectedSerialNos, setSelectedSerialNos])

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        const selectedSerialNumbers = selectedSerialNos?.map(t => t.serialNo);
        return entries.map(e => ({
            key: e.serialNumber,
            data: e,
            cells: {
                [DataGridExoticColumnFields.selection]: null,
                [DecreaseSerializedProductAvailabilityDataGridCellKeys.serial]: e.serialNumber,
                [DecreaseSerializedProductAvailabilityDataGridCellKeys.cost]: selectedSerialNumbers?.includes(e.serialNumber)
                    ? <Paper>
                        <OutlinedInput
                            size={"small"}
                            type={"number"}
                            value={e.unitCost}
                            onChange={(value) => handleSerialCostChange(e.serialNumber, value)}
                            onBlur={(t) => handleSerialCostBlurred(t, e.serialNumber)}
                            InputProps={{
                                startAdornment: <span>$</span>
                            }}
                        />
                    </Paper>
                    : formatMoney(e?.unitCost),
                [DecreaseSerializedProductAvailabilityDataGridCellKeys.receivedDate]: e.receivedDate,
            }
        }))
    }, [entries, handleSerialCostBlurred, selectedSerialNos])

    return (
        <>
            <div className={'d-flex w-100 flex-column'}>
                {
                    showLastSection &&
                    <div className={'d-flex w-100 titles-section'}>
                        <Col xs={3}>
                            Current Availability
                        </Col>
                        <Col xs={1}/>
                        <Col xs={3}>
                            Removed Serials
                        </Col>
                        <Col xs={1}/>
                        <Col xs={3}>
                            New Availability
                        </Col>
                    </div>
                }
                {
                    showLastSection &&
                    <div className={'d-flex w-100'}>
                        <Col xs={3}>
                            <div className={'info-block'}>
                                {currentAvailability}
                            </div>
                        </Col>
                        <Col xs={1}>
                            <p className={'math-icon'}>
                                -
                            </p>
                        </Col>
                        <Col xs={3}>
                            <div className={'info-block'}>
                                {selectedSerialNos?.length ?? 0}
                            </div>
                        </Col>
                        <Col xs={1}>
                            <p className={'math-icon'}>
                                =
                            </p>
                        </Col>
                        <Col xs={3}>
                            <div className={'info-block'}>
                                {(currentAvailability ?? 0) - (selectedSerialNos?.length ?? 0)}
                            </div>
                        </Col>
                    </div>
                }
                {
                    showLastSection &&
                    <div className={'d-flex flex-column w-100 mt-5 justify-content-center align-items-center'}>
                        <div className={'d-flex align-items-center justify-content-center w-100 mb-3'}>
                            <p className={'info-chips-title mr-4'}>
                                Average Cost
                            </p>
                            <div className={'info-chips ml-3'}>
                                {`${formatMoney(keyInformation.averageCost)}`}
                            </div>
                        </div>
                        <div className={'d-flex align-items-center justify-content-center w-100 mb-3'}>
                            <p className={'info-chips-title mr-4'}>
                                Extended Cost
                            </p>
                            <div className={'info-chips ml-2'}>
                                {`${formatMoney(-selectedSerialNos.reduce((prev, current) => prev + (current.cost ?? 0), 0))}`}
                            </div>
                        </div>
                        <div className={'d-flex align-items-center justify-content-center w-100 mb-3'}>
                            <p className={'info-chips-title mr-4'}>
                                Selected Items
                            </p>
                            <div className={'info-chips ml-1'}>
                                {selectedSerialNos?.length ?? 0}
                            </div>
                        </div>
                    </div>
                }
                {
                    (showLastSection && !hideReasonNoteSection) &&
                    <div className={'w-80 mx-auto'}>
                        <ReasonNoteSection
                            formId={formId}
                            submitting={modifying}
                            onSubmit={onSubmitReasonNotesForm}
                        />
                    </div>
                }
                {
                    !showLastSection &&
                    <>
                        <p className={'text-md font-weight-semi-bold mt-1 px-3'}>
                            Select Serials to remove
                        </p>
                        <SearchInput
                            loading={loading}
                            search={setKeywords}
                            autoSearch
                            disabled={loading || modifying}
                            initialValue={keywords ?? ''}
                            variant={'outlined'}
                            className={'my-3'}
                        />
                        <PartnerDataGrid
                            ref={dataGridController}
                            {...dataGridSaveConfig}
                            visibleRows={5}
                            initialLayoutState={{
                                columns: columns,
                                density: DataGridDensities.compact,
                            }}
                            hideToolbar
                            columns={dataGridColumns}
                            rows={dataGridRows}
                            state={{
                                pagination: paginationInfo,
                                sortBy: orderBy,
                                loading: {state: loading || fetchingAll},
                                density: dataGridDensity ?? DataGridDensities.compact,
                            }}
                            onSortByChanged={(_, current) => onSort(current)}
                            onCurrentPageChanged={(_, current) => setPaginationInfo(current)}
                            onPageSizeChanged={(_, current) => setPaginationInfo(current)}
                            onRowSelectionInfoChanged={onRowSelectionInfoChanged}
                        />
                    </>
                }
            </div>
        </>
    );
}

export default DecreaseProductAvailabilityDialogSerializableSection;
